import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Save } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const CATEGORIES = ["COSMOLOGY", "CREWED", "ROBOTIC", "OBSERVATORY", "SATELLITE", "SCIENCE"];

/** Admin-only modal form for creating or editing a milestone. */
export default function MilestoneForm({ initial, defaultMonth, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    month: initial?.month || defaultMonth || 1,
    year: initial?.year ?? new Date().getUTCFullYear(),
    display_date: initial?.display_date || "",
    description: initial?.description || "",
    category: initial?.category || "SCIENCE",
    image_url: initial?.image_url || "",
    source_url: initial?.source_url || "",
  });
  const [saving, setSaving] = useState(false);
  const editing = !!initial;

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        month: parseInt(form.month, 10),
        year: parseInt(form.year, 10),
        image_url: form.image_url || null,
        source_url: form.source_url || null,
      };
      if (editing) {
        await api.put(`/milestones/${initial.id}`, payload);
        toast.success("Milestone updated");
      } else {
        await api.post("/milestones", payload);
        toast.success("Milestone created");
      }
      onSaved();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="milestone-form-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={submit}
        className="sat-brackets sat-surface border border-[color:var(--sat-cyan)] p-5 md:p-6 w-full max-w-2xl my-8 sat-box-glow"
        data-testid="milestone-form"
      >
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-[color:var(--sat-border-bright)]">
          <div>
            <div className="sat-label sat-cyan">DIRECTIVE // {editing ? "AMEND RECORD" : "NEW RECORD"}</div>
            <h2 className="font-display font-bold text-xl mt-1">
              {editing ? "Edit Milestone" : "New Milestone"}
            </h2>
          </div>
          <button type="button" onClick={onClose}
                  className="text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-cyan)]"
                  data-testid="milestone-form-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="col-span-1 md:col-span-2">
            <div className="sat-label mb-1">TITLE</div>
            <input required value={form.title} onChange={set("title")} data-testid="mf-title" />
          </label>

          <label>
            <div className="sat-label mb-1">MONTH (1-12)</div>
            <input type="number" min="1" max="12" required value={form.month}
                   onChange={set("month")} data-testid="mf-month" />
          </label>
          <label>
            <div className="sat-label mb-1">YEAR (negative for BCE)</div>
            <input type="number" required value={form.year} onChange={set("year")} data-testid="mf-year" />
          </label>

          <label className="col-span-1 md:col-span-2">
            <div className="sat-label mb-1">DISPLAY DATE (e.g. "Jul 20, 1969" or "13.8B years ago")</div>
            <input required value={form.display_date} onChange={set("display_date")} data-testid="mf-display-date" />
          </label>

          <label>
            <div className="sat-label mb-1">CATEGORY</div>
            <select value={form.category} onChange={set("category")} data-testid="mf-category"
                    className="!bg-[color:var(--sat-surface)] !border !border-[color:var(--sat-border)] !text-[color:var(--sat-text)] !font-mono !text-[13px] !py-3 !px-3.5 !w-full">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>
            <div className="sat-label mb-1">IMAGE URL (optional)</div>
            <input value={form.image_url} onChange={set("image_url")} data-testid="mf-image" placeholder="https://…" />
          </label>

          <label className="col-span-1 md:col-span-2">
            <div className="sat-label mb-1">SOURCE URL (optional)</div>
            <input value={form.source_url} onChange={set("source_url")} data-testid="mf-source" placeholder="https://…" />
          </label>

          <label className="col-span-1 md:col-span-2">
            <div className="sat-label mb-1">DESCRIPTION</div>
            <textarea required rows={4} value={form.description} onChange={set("description")}
                      data-testid="mf-description" />
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-[color:var(--sat-border-bright)]">
          <button type="button" onClick={onClose}
                  className="sat-btn" data-testid="milestone-form-cancel">CANCEL</button>
          <button type="submit" disabled={saving}
                  className="sat-btn sat-btn-solid disabled:opacity-50"
                  data-testid="milestone-form-save">
            <Save size={13} /> {saving ? "SAVING…" : (editing ? "UPDATE" : "CREATE")}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
